/**
 * st-mobile-menu.js (Sportstech, additive) - ST 2026-10-13 mobile menu phase 2 (Wrike 4582273669)
 *
 * Behaviour of the new phone menu (snippets/st-mobile-menu.liquid, <= 430 px, inside #MenuDrawer):
 * - Accordions: [data-st-mm-acc] buttons toggle the sub-list below them (aria-expanded).
 * - Product views: a[data-st-mm-view] opens the view of that menu item instead of following the link. The products
 *   of the linked collection are loaded only now through the Section Rendering API (sections/st-menu-products.liquid,
 *   best-selling) and cached per collection for the page view. Without JS the links go to the collection page.
 * - A view shows the products assigned to it in custom.menu_chips ("view" or "view:chip"); when no product of the
 *   collection is assigned to the view, all products of the collection are shown.
 * - Order in every view and chip (Ali 14.10.2026): net revenue rank (custom.menu_revenue_rank, 1 = highest), then
 *   products without revenue in the collection's best-selling order; sold-out products go last, in the same order.
 * - Chips: "Alle" plus at most 4 chips from the view config, only with >= 5 products and only chips with products.
 *   An entry "-" in the chips text is a line break (the design breaks the treadmill chips after the 2nd chip).
 *   Single choice (radio group). The counter text and the quiz tile position follow the visible products.
 * - Card tag while a Weekly Deal runs (end from the product data, checked again in the browser): filled
 *   "Top Deal −X %" as the product cards; coupons get no tag (Wrike 4584735183, round 10).
 * - Images (accordion thumbnails and cards): the shop images have a wide white margin, so the device would fill only about
 *   60 % of its box. trimImages() loads each image once, finds the device on a small canvas (difference to the corner
 *   colour, as the prototype's trim_*.jpg) and shows the cropped part (same-origin /cdn/shop URLs; CORS is allowed). If
 *   that fails, the uncropped image is shown.
 * - Preview chips (header setting st_menu_v2_preview_chips, preview only): "handle: view, view:chip" per line for
 *   products without custom.menu_chips. The key can also be the product ID, which is the same in every market
 *   (handles are translated in fr/es/it/nl).
 * - Preview rank (header setting st_menu_v2_preview_rank, preview only): one product ID per line, highest revenue first,
 *   for products without custom.menu_revenue_rank.
 * - Live-Chat pill (#open-chat): closes the menu before assets/st-open-chat.js opens the chat.
 * - Closing the drawer returns to the main menu.
 * - window.stMobileMenu (Wrike 4583400208): the same views, product lists and chips for the subcategory rows of the
 *   collection pages (assets/st-category-chips.js), so both always show the same.
 * Rollback: see snippets/st-mobile-menu.liquid.
 */
(function () {
  "use strict";

  const nav = document.querySelector("[data-st-mm]");
  if (!nav) return;
  const configEl = nav.querySelector("[data-st-mm-config]");
  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  // Liquid's t filter HTML-escapes ("Walken &amp; Joggen"); decode once, esc() escapes again when rendering.
  const decoder = document.createElement("textarea");
  function decode(value) {
    if (typeof value === "string") {
      decoder.innerHTML = value;
      return decoder.value;
    }
    if (value && typeof value === "object") {
      Object.keys(value).forEach(function (k) {
        value[k] = decode(value[k]);
      });
    }
    return value;
  }
  decode(config);

  const drawer = nav.closest("menu-drawer");
  const main = nav.querySelector("[data-st-mm-main]");
  const panel = nav.querySelector("[data-st-mm-panel]");
  const scroller = nav.closest(".drawer__content") || nav;
  const cache = {};
  const trimmed = {};
  const preview = parsePreview(config.previewChips);
  const previewRank = parseRank(config.previewRank);
  let current = null;
  let returnFocus = null;
  let returnScroll = 0;

  const CHEV =
    '<svg class="st-mm__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M10 6L16 12L10 18"/></svg>';
  const BACK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M14 6L8 12L14 18"/></svg>';
  const QUIZ =
    '<svg class="st-mm__qi" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4.5"/><circle cx="12" cy="17.6" r=".6" fill="currentColor"/></svg>';

  function esc(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c];
    });
  }

  function sectionUrl(url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("section_id", config.section);
    u.searchParams.set("sort_by", "best-selling");
    return u.toString();
  }

  function load(url) {
    if (!cache[url]) {
      cache[url] = fetch(sectionUrl(url), { credentials: "same-origin" })
        .then(function (res) {
          if (!res.ok) throw new Error("HTTP " + res.status);
          return res.text();
        })
        .then(function (html) {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const data = doc.querySelector("[data-st-menu-products]");
          if (!data) throw new Error("no data");
          return JSON.parse(data.textContent);
        })
        .catch(function (err) {
          delete cache[url];
          throw err;
        });
    }
    return cache[url];
  }

  function parsePreview(text) {
    const map = {};
    String(text || "")
      .split(/\r?\n/)
      .forEach(function (line) {
        const at = line.indexOf(":");
        if (at < 1) return;
        const handle = line.slice(0, at).trim();
        const tags = line
          .slice(at + 1)
          .split(",")
          .map(function (t) {
            return t.trim();
          })
          .filter(Boolean);
        if (handle && tags.length && !map[handle]) map[handle] = tags;
      });
    return map;
  }

  function parseRank(text) {
    const map = {};
    String(text || "")
      .split(/\r?\n/)
      .map(function (line) {
        return line.trim();
      })
      .filter(Boolean)
      .forEach(function (id, i) {
        if (!map[id]) map[id] = i + 1;
      });
    return map;
  }

  // Crops the margin in the colour of the top left corner; resolves with a blob URL or null.
  function cropUrl(src) {
    if (!trimmed[src]) {
      trimmed[src] = new Promise(function (resolve) {
        const im = new Image();
        im.crossOrigin = "anonymous";
        im.onerror = function () {
          resolve(null);
        };
        im.onload = function () {
          try {
            const w = im.naturalWidth;
            const h = im.naturalHeight;
            const f = Math.min(1, 160 / Math.max(w, h));
            const sw = Math.max(1, Math.round(w * f));
            const sh = Math.max(1, Math.round(h * f));
            const probe = document.createElement("canvas");
            probe.width = sw;
            probe.height = sh;
            const ctx = probe.getContext("2d", { willReadFrequently: true });
            ctx.drawImage(im, 0, 0, sw, sh);
            const d = ctx.getImageData(0, 0, sw, sh).data;
            const i0 = (2 * sw + 2) * 4;
            let x0 = sw;
            let y0 = sh;
            let x1 = -1;
            let y1 = -1;
            for (let y = 0; y < sh; y += 1) {
              for (let x = 0; x < sw; x += 1) {
                const i = (y * sw + x) * 4;
                const diff = Math.max(
                  Math.abs(d[i] - d[i0]),
                  Math.abs(d[i + 1] - d[i0 + 1]),
                  Math.abs(d[i + 2] - d[i0 + 2]),
                  Math.abs(d[i + 3] - d[i0 + 3]),
                );
                if (diff > 14) {
                  if (x < x0) x0 = x;
                  if (x > x1) x1 = x;
                  if (y < y0) y0 = y;
                  if (y > y1) y1 = y;
                }
              }
            }
            // Nothing found or hardly any margin: keep the original.
            if (x1 < 0 || (x1 - x0 + 1) * (y1 - y0 + 1) > sw * sh * 0.9)
              return resolve(null);
            const cx = Math.max(0, Math.floor((x0 - 1) / f));
            const cy = Math.max(0, Math.floor((y0 - 1) / f));
            const cw = Math.min(w, Math.ceil((x1 + 2) / f)) - cx;
            const ch = Math.min(h, Math.ceil((y1 + 2) / f)) - cy;
            const out = document.createElement("canvas");
            out.width = cw;
            out.height = ch;
            out.getContext("2d").drawImage(im, cx, cy, cw, ch, 0, 0, cw, ch);
            out.toBlob(function (blob) {
              resolve(blob ? URL.createObjectURL(blob) : null);
            });
          } catch (e) {
            resolve(null);
          }
        };
        im.src = src;
      });
    }
    return trimmed[src];
  }

  function trimImages(root) {
    root.querySelectorAll("[data-st-mm-trim]").forEach(function (box) {
      const src = box.getAttribute("data-st-mm-trim");
      box.removeAttribute("data-st-mm-trim");
      cropUrl(src).then(function (url) {
        const img = document.createElement("img");
        img.alt = "";
        img.decoding = "async";
        img.src = url || src;
        box.appendChild(img);
      });
    });
  }

  function chipsOf(view) {
    return String(view.chips || "")
      .split("|")
      .map(function (part) {
        if (part.trim() === "-") return ["-", ""];
        const i = part.indexOf(":");
        return i > 0
          ? [part.slice(0, i).trim(), part.slice(i + 1).trim()]
          : null;
      })
      .filter(function (c) {
        return c && c[0] && (c[1] || c[0] === "-");
      });
  }

  function productsOf(key, items) {
    items.forEach(function (p) {
      const pre = preview[p.h] || preview[String(p.id)];
      if (pre && !(p.m && p.m.length)) p.m = pre;
      if (!p.r) p.r = previewRank[String(p.id)] || 0;
    });
    let tagged = items.filter(function (p) {
      return (p.m || []).some(function (m) {
        return m === key || m.indexOf(key + ":") === 0;
      });
    });
    const list = tagged.length ? tagged : items;
    // Available before sold-out (Ali 12.10.2026, decision 8), then revenue rank (Ali 14.10.2026), then the
    // best-selling position of the collection for products without revenue (stable).
    return list
      .map(function (p, i) {
        return [p.a ? 0 : 1, p.r > 0 ? p.r : Infinity, i, p];
      })
      .sort(function (a, b) {
        return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
      })
      .map(function (e) {
        return e[3];
      });
  }

  function countText(view, n) {
    return (n === 1 ? view.one : view.other).replace("%n", n);
  }

  function card(p) {
    const now = Date.now() / 1000;
    const live = p.e && p.e > now;
    // Wrike 4592172809: one tag, same order as the desktop cards: lightning deal (window runs) > Halloween (code
    // still valid) > Top Deal. Start and end are checked here, so an expired tag drops out; never a tag without text.
    let text = "";
    let mod = "";
    if (p.fs && p.fs <= now && now < p.fe && config.flashBadge) {
      text = config.flashBadge;
      mod = " st-mm__fd--flash";
    } else if (p.hw && p.hw > now && config.hwBadge) {
      text = config.hwBadge;
      mod = " st-mm__fd--hw";
    } else if (live && p.k === "deal" && p.d > 0 && config.dealBadge) {
      text = config.dealBadge.replace("%p", p.d);
    }
    const badge = text.trim()
      ? '<span class="st-mm__fd' + mod + '">' + esc(text) + "</span>"
      : "";
    const img = p.i
      ? '<span class="st-mm__img" data-st-mm-trim="' + esc(p.i) + '"></span>'
      : "";
    const f = (p.f || []).filter(Boolean).map(esc).join("<br>");
    return (
      '<li class="st-mm__pc' +
      (p.a ? "" : " st-mm__pc--soldout") +
      '"><a href="' +
      esc(p.u) +
      '">' +
      badge +
      '<span class="st-mm__im">' +
      img +
      "</span>" +
      '<span class="st-mm__nm">' +
      esc(p.t) +
      "</span>" +
      '<span class="st-mm__sp">' +
      f +
      "</span>" +
      '<span class="st-mm__pr">' +
      esc(p.p) +
      (p.s ? " <s>" + esc(p.s) + "</s>" : "") +
      "</span>" +
      "</a></li>"
    );
  }

  function quizTile(view) {
    return (
      '<li class="st-mm__qz"><a href="' +
      esc(view.quizUrl) +
      '">' +
      QUIZ +
      '<span class="st-mm__qt"><b>' +
      esc(config.quizTitle) +
      "</b><span>" +
      esc(view.quiz) +
      "</span></span>" +
      '<span class="st-mm__qa" aria-hidden="true">›</span></a></li>'
    );
  }

  // Products of one chip of a view ("all" = the whole view list).
  function chipProducts(key, list, chip) {
    return chip === "all"
      ? list
      : list.filter(function (p) {
          return (p.m || []).indexOf(key + ":" + chip) !== -1;
        });
  }

  // Chips the view shows: at most 4 with products, only from 5 products on; "-" = line break between two chips.
  function shownChips(view, key, list) {
    let chips = [];
    if (list.length >= 5) {
      let shown = 0;
      chips = chipsOf(view).filter(function (c) {
        if (c[0] === "-") return true;
        const ok =
          shown < 4 &&
          list.some(function (p) {
            return (p.m || []).indexOf(key + ":" + c[0]) !== -1;
          });
        if (ok) shown += 1;
        return ok;
      });
      // A line break ("-", as in the design after "Profi") only between two chips.
      chips = chips.filter(function (c, i) {
        return (
          c[0] !== "-" ||
          (i > 0 &&
            chips[i - 1][0] !== "-" &&
            chips.slice(i + 1).some(function (n) {
              return n[0] !== "-";
            }))
        );
      });
      if (!shown) chips = [];
    }
    return chips;
  }

  function renderGrid(chip) {
    const view = current.view;
    const shown = chipProducts(current.key, current.list, chip);
    const html = shown.map(card);
    if (view.quiz && view.quizUrl) {
      // Same position as in the prototype: after 6 cards from 8 on, after 4 from 5 on, else at the end.
      const at = shown.length >= 8 ? 6 : shown.length >= 5 ? 4 : shown.length;
      html.splice(at, 0, quizTile(view));
    }
    const grid = panel.querySelector("[data-st-mm-grid]");
    grid.innerHTML = html.join("");
    trimImages(grid);
    panel.querySelector("[data-st-mm-count]").textContent = countText(
      view,
      shown.length,
    );
    panel.querySelectorAll("[data-st-mm-chip]").forEach(function (btn) {
      const on = btn.getAttribute("data-st-mm-chip") === chip;
      btn.classList.toggle("is-on", on);
      btn.setAttribute("aria-checked", on ? "true" : "false");
      btn.tabIndex = on ? 0 : -1;
    });
  }

  function renderView(items) {
    const view = current.view;
    const key = current.key;
    current.list = productsOf(key, items);
    const chips = shownChips(view, key, current.list);
    const chipHtml = chips.length
      ? '<div class="st-mm__chips" role="radiogroup" aria-label="' +
        esc(config.filter) +
        '">' +
        [["all", config.all]]
          .concat(chips)
          .map(function (c) {
            if (c[0] === "-")
              return '<span class="st-mm__brk" aria-hidden="true"></span>';
            return (
              '<button type="button" class="st-mm__chip" role="radio" aria-checked="false" data-st-mm-chip="' +
              esc(c[0]) +
              '">' +
              esc(c[1]) +
              "</button>"
            );
          })
          .join("") +
        "</div>"
      : "";
    panel.querySelector("[data-st-mm-body]").innerHTML =
      chipHtml +
      '<p class="st-mm__cnt" data-st-mm-count aria-live="polite"></p>' +
      '<ul class="st-mm__grid" role="list" data-st-mm-grid></ul>' +
      '<a class="st-mm__all" href="' +
      esc(view.url) +
      '">' +
      esc(view.allLabel) +
      CHEV +
      "</a>";
    renderGrid("all");
  }

  function renderError() {
    const view = current.view;
    panel.querySelector("[data-st-mm-body]").innerHTML =
      '<p class="st-mm__cnt">' +
      esc(config.error) +
      "</p>" +
      '<a class="st-mm__all" href="' +
      esc(view.url) +
      '">' +
      esc(view.allLabel) +
      CHEV +
      "</a>";
  }

  function openView(key, trigger) {
    const view = config.views[key];
    if (!view) return false;
    current = { key: key, view: view, list: [] };
    returnFocus = trigger;
    returnScroll = scroller.scrollTop;
    panel.innerHTML =
      '<button type="button" class="st-mm__back" data-st-mm-back>' +
      BACK +
      '<span class="sr-only">' +
      esc(config.back) +
      ": </span><span>" +
      esc(view.title) +
      "</span></button>" +
      '<div class="st-mm__vbody" data-st-mm-body><p class="st-mm__cnt" aria-live="polite">' +
      esc(config.loading) +
      "</p>" +
      '<ul class="st-mm__grid st-mm__grid--loading" role="list" aria-hidden="true"><li></li><li></li><li></li><li></li></ul></div>';
    main.hidden = true;
    panel.hidden = false;
    panel.classList.remove("is-in");
    void panel.offsetWidth;
    panel.classList.add("is-in");
    scroller.scrollTop = 0;
    panel.querySelector("[data-st-mm-back]").focus({ preventScroll: true });
    const token = current;
    load(view.url)
      .then(function (items) {
        if (current === token) renderView(items);
      })
      .catch(function () {
        if (current === token) renderError();
      });
    return true;
  }

  function closeView(focus) {
    if (!current) return;
    current = null;
    panel.hidden = true;
    panel.innerHTML = "";
    main.hidden = false;
    scroller.scrollTop = returnScroll;
    if (focus && returnFocus) returnFocus.focus({ preventScroll: true });
  }

  nav.addEventListener("click", function (event) {
    const acc = event.target.closest("[data-st-mm-acc]");
    if (acc) {
      const list = document.getElementById(acc.getAttribute("aria-controls"));
      const open = acc.getAttribute("aria-expanded") !== "true";
      acc.setAttribute("aria-expanded", open ? "true" : "false");
      if (list) {
        list.hidden = !open;
        if (open) trimImages(list);
      }
      return;
    }
    const viewLink = event.target.closest("a[data-st-mm-view]");
    if (viewLink && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      if (openView(viewLink.getAttribute("data-st-mm-view"), viewLink))
        event.preventDefault();
      return;
    }
    if (event.target.closest("[data-st-mm-back]")) {
      closeView(true);
      return;
    }
    const chip = event.target.closest("[data-st-mm-chip]");
    if (chip && current && current.list.length) {
      renderGrid(chip.getAttribute("data-st-mm-chip"));
    }
  });

  // Arrow keys move within the chip radio group.
  nav.addEventListener("keydown", function (event) {
    const chip = event.target.closest("[data-st-mm-chip]");
    if (
      !chip ||
      ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].indexOf(event.key) ===
        -1
    )
      return;
    const all = Array.prototype.slice.call(
      chip.parentNode.querySelectorAll("[data-st-mm-chip]"),
    );
    const step = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
    const next = all[(all.indexOf(chip) + step + all.length) % all.length];
    event.preventDefault();
    renderGrid(next.getAttribute("data-st-mm-chip"));
    next.focus();
  });

  // Capture on window runs before the document capture listener of assets/st-open-chat.js.
  window.addEventListener(
    "click",
    function (event) {
      const chat =
        event.target.closest && event.target.closest('a[href="#open-chat"]');
      if (
        chat &&
        nav.contains(chat) &&
        drawer &&
        typeof drawer.hide === "function"
      )
        drawer.hide();
    },
    true,
  );

  // ST 2026-10-18 (Wrike 4583400208): the subcategory rows of the collection pages (assets/st-category-chips.js) use
  // exactly this data, so menu and page always show the same chips, labels and products (one source).
  // data(key) resolves to { view, list, chips } (list in menu order, chips as the view shows them, without "-").
  window.stMobileMenu = {
    views: config.views,
    all: config.all,
    data: function (key) {
      const view = config.views[key];
      if (!view) return Promise.reject(new Error("no view"));
      return load(view.url).then(function (items) {
        const list = productsOf(key, items);
        return {
          view: view,
          list: list,
          chips: shownChips(view, key, list).filter(function (c) {
            return c[0] !== "-";
          }),
        };
      });
    },
    products: chipProducts,
  };
  document.dispatchEvent(new CustomEvent("st-mobile-menu:ready"));

  if (drawer) {
    new MutationObserver(function () {
      if (!drawer.hasAttribute("open")) closeView(false);
    }).observe(drawer, { attributes: true, attributeFilter: ["open"] });
  }
})();
